/**
 * The client credentials the LiveSocket authenticates with, and a record of
 * which pair the socket actually connected with.
 *
 * Why this exists as shared state rather than a local in app.js: two things
 * need it. app.js reads it from the LiveSocket `params` closure, and the
 * ExtensionBridge hook writes it when the extension port hands over a fresh
 * pair. Before, the hook had no way to update the credentials app.js would
 * send, so its "reconnect with the new credentials" path would have resent the
 * old ones.
 *
 * The rules that make this work:
 *
 *   - `BrainboxLive` authenticates purely from connect params — see
 *     `fetch_credentials/2`, whose session argument is ignored. There is no
 *     server-side session to fall back on.
 *   - LiveSocket re-evaluates its `params` option on every socket connect, so
 *     whatever is set here at connect time is what the server validates.
 *   - `params` is also re-read on every channel join and rejoin, and
 *     `get_connect_params/1` reads the JOIN payload — so a LiveView auto-rejoin
 *     after a channel error picks up whatever is set here with no socket cycle.
 *   - Consequently a change here only reaches the server on the next connect or
 *     rejoin. Forcing that is the only reason to ever cycle the socket.
 */

let credentials = { client_id: null, client_token: null };

// The credentials the most recent connect/join actually carried. Null until the
// LiveSocket has evaluated its `params` option at least once.
let sentCredentials = null;

// Set when the credentials came from URL params, which exist to authenticate as
// a chosen user for dev/test. The extension must not quietly swap that for
// whoever is signed into Wavebox, so a pinned pair wins over anything the
// extension port later offers.
let pinned = false;

export function getCredentials () {
  return { ...credentials };
}

export function setCredentials (authData) {
  credentials = {
    client_id: authData?.client_id || null,
    client_token: authData?.client_token || null
  };
}

/**
 * Seed from the dev/test URL params. Pins if both values are present, so the
 * extension cannot override an explicitly requested identity.
 */
export function pinCredentials (authData) {
  setCredentials(authData);
  pinned = hasCredentials();
}

export function hasCredentials () {
  return !!(credentials.client_id && credentials.client_token);
}

/**
 * Called from the LiveSocket `params` closure. Records what is about to go on
 * the wire so `socketNeedsReconnect` can tell a genuine credential change from
 * a repeat of the pair the connection already holds.
 */
export function recordCredentialsSent () {
  sentCredentials = { ...credentials };
}

/**
 * Whether the socket must be cycled for the server to see `authData`.
 *
 * False for the normal launch, where app.js has already awaited the extension
 * credentials and connected with them — reconnecting there buys nothing and
 * costs a visible disconnect, a duplicate mount, and a duplicate Wavebox API
 * validation. True only when the extension hands over a pair the current
 * connection is not carrying, which is what a token refresh or a switch of
 * signed-in user looks like from here.
 */
export function socketNeedsReconnect (authData) {
  if (pinned) { return false; }
  if (!authData?.client_id || !authData?.client_token) { return false; }
  if (!sentCredentials) { return true; }

  return (
    sentCredentials.client_id !== authData.client_id ||
    sentCredentials.client_token !== authData.client_token
  );
}
